import React, { useCallback, useState } from 'react';
import { Alert, Link, Text } from '@tonic-one/react';
import { Trans } from 'react-i18next';
import customConfig from 'src/config';
import { THIRD_PARTY_LINK } from '@Utils/casDocLink';

/**
 * Reminder for admins to deploy the Outlook Add-in. The banner is dismissible
 * but uses sessionStorage rather than localStorage: the dismiss is honored
 * for the rest of this browser session only, and the banner reappears on
 * the next sign-in (new tab / new session). That keeps later admins from
 * being silently blocked by a previous admin's permanent dismiss while still
 * letting the current user de-clutter the page once they have read the copy.
 *
 * Body is a single sentence with two inline placeholders (<1> = "Microsoft
 * 365 admin center" doc link, <2> = manifest URL link). Keeping it on one
 * paragraph matches the Figma spec — the banner stays single-line on wide
 * viewports and only wraps when the viewport itself forces it. The links stay
 * inline (no display="inline-block") so that a long manifest URL flows with
 * the surrounding sentence rather than getting pushed onto its own line.
 */

// per-tab dismiss flag — sessionStorage clears when the tab/window closes,
// so the banner returns on next sign-in without ever surviving across
// browser restarts.
const DISMISS_KEY = 'cas_smart_alert_deploy_banner_dismissed';

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    // sessionStorage unavailable (private mode / quota) — fail open: show banner.
    return false;
  }
}

function writeDismissed() {
  try {
    sessionStorage.setItem(DISMISS_KEY, '1');
  } catch {
    // best-effort; if storage fails the banner just reappears on remount.
  }
}

const DeployAddInBanner: React.FC = () => {
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed());

  const handleClose = useCallback(() => {
    writeDismissed();
    setDismissed(true);
  }, []);

  if (dismissed) return null;

  return (
    <Alert
      severity="info"
      variant="outline"
      mt="4x"
      isClosable
      onClose={handleClose}
    >
      <Text>
        <Trans
          i18nKey="globalSetting.misdirectedEmailDetection.deployBanner.body"
          values={{
            manifestUrl:
              customConfig.ui.administrator.smartAlertAddInInstallLink,
          }}
        >
          placeholder
          <Link href={THIRD_PARTY_LINK.addInIntegratedApps} target="_blank">
            placeholder
          </Link>
          <Link
            target="_blank"
            href={customConfig.ui.administrator.smartAlertAddInInstallLink}
          >
            placeholder
          </Link>
        </Trans>
      </Text>
    </Alert>
  );
};

export default DeployAddInBanner;
